import React from 'react'
import axios, { isAxiosError } from 'axios'
import { OpaqueSparqlResults } from './sparqlResultHelpers'
import { DeferredPromise } from './DeferredPromise'
import { Signal } from '../utils/signals'
import { freezeInDevMode } from '../utils/dev-mode'
import { registerEventLog } from '../components/EventLogs/event-log-store'
import { formatTimestamp } from '../utils/format-timestamp'
import { ComponentNamespace, useNamespace } from '../redux/pubsub'

export type SparqlResultHolder = {
  result: OpaqueSparqlResults
  isLatest: () => boolean
  isError: boolean
}

const ERROR_RESULT = {
  head: {
    vars: []
  },
  results: {
    bindings: []
  }
} satisfies OpaqueSparqlResults

type QueryString = string

type TemplateId = string

export class ErrorNeedAuth extends Error {}

const state = {
  instances: new Map<TemplateId, SparqlManager>()
}

type LastFetchRequest = {
  query: string
  promise: PromiseLike<SparqlResultHolder>
}

/**
 * This is used to indicate the status of the data pipeline.
 * `processed` is special in that it's signalled outside of this class.
 */
type Status = 'init' | 'fetching' | 'fetched' | 'processed'

export function useSparqlManager(sparqlEndpoint: string, templateId: TemplateId) {
  const namespace = useNamespace()
  const ref = React.useRef<SparqlManager>()
  if (!ref.current) ref.current = new SparqlManager(sparqlEndpoint, templateId, namespace)
  return ref.current! // ! because ref.current is assigned above
}

export class SparqlManager {
  private readonly templateId: string
  private readonly sparqlEndpoint: string
  private readonly namespace: ComponentNamespace
  private readonly cache = new Map<QueryString, OpaqueSparqlResults>()

  private lastRequest: LastFetchRequest | null = null
  private cancelActiveRequest: null | (() => void) = null

  private lastResult: SparqlResultHolder | null = null

  public readonly dataSignal = new Signal<SparqlResultHolder>()
  public readonly statusSignal = new Signal<Status>()

  private status: Status = 'init'

  constructor(sparqlEndpoint: string, templateId: TemplateId, namespace: ComponentNamespace) {
    this.sparqlEndpoint = sparqlEndpoint
    this.templateId = templateId
    this.namespace = namespace
    state.instances.set(templateId, this)
  }

  getSnapshot(): SparqlResultHolder | null {
    return this.lastResult
  }

  getStatus = () => this.status

  private setStatus = (status: Status) => {
    this.status = status
    this.statusSignal.emit(status)
  }

  setProcessed = (data: OpaqueSparqlResults) => {
    if (this.status !== 'fetched') return
    if (this.lastResult?.result !== data) return
    this.setStatus('processed')
  }

  getLastResult = () => this.lastResult

  async fetch(query: QueryString): Promise<SparqlResultHolder> {
    if (this.lastRequest?.query === query) {
      return this.lastRequest.promise
    }

    this.setStatus('fetching')
    const promise = new DeferredPromise<SparqlResultHolder>()
    promise.then(resultHolder => {
      const timestamp = new Date()
      registerEventLog({
        type: 'sparql-request-event',
        request: query,
        templateId: this.templateId,
        response: JSON.stringify(resultHolder.result, null, 4),
        timestamp: formatTimestamp(timestamp),
        namespace: this.namespace,
      })
      this.setStatus('fetched')
      this.dataSignal.emit(resultHolder)
    })

    this.lastRequest = { query, promise }

    const isLatest = () => promise === this?.lastRequest?.promise
    this.cancelActiveRequest?.()

    if (this.cache.has(query)) {
      console.info('This query is cached. Returning cached result.', this.sparqlEndpoint, query)
      const cachedResults = this.cache.get(query)
      const isError = !cachedResults
      const result = isError ? ERROR_RESULT : cachedResults
      setTimeout(() => promise.resolve({
        isLatest,
        isError,
        result: freezeInDevMode(result)
      }), 10)
      return promise
    }

    try {
      const data = new URLSearchParams()
      data.set('infer', String(false))
      data.set('sameAs', String(false))
      data.set('query', query)

      const response = await axios({
        method: 'post',
        url: this.sparqlEndpoint,
        maxContentLength: Infinity,
        maxBodyLength: Infinity,
        data,
        // @ts-expect-error
        withCredentials: document.withCredentials ?? true,
        headers:{
          Accept: 'application/sparql-results+json,*/*;q=0.9',
          'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
        },
        cancelToken: new axios.CancelToken((c) => this.cancelActiveRequest = c),
      })

      const match = query.match(/#\s*artificially-slow-query\s*=\s*([0-9]+)/)
      if (match) {
        await waitFor(parseInt(match[1]))
      }

      this.cancelActiveRequest = null
      if (!response.data) {
        console.error("SPARQL query did not return valid results: most likely the query contains an error:\n", query)
      }

      const isError = !response.data
      const result = isError ? ERROR_RESULT : response.data

      if (shouldCacheQuery(query)) {
        this.cache.set(query, response.data)
      }

      const resultHolder: SparqlResultHolder = {
        isLatest,
        isError,
        result: freezeInDevMode(result) // I expect some mutation shenanigans, let us error them in dev mode
      }
      this.lastResult = resultHolder
      promise.resolve(resultHolder)
      return promise
    } catch (error) {
      if (isAxiosError(error) && (error.response?.status === 401 || error.response?.status === 403))
        throw new ErrorNeedAuth()
      throw error
    }
  }

  async fetchTurtle(query: QueryString): Promise<string> {
    try {
      const data = new URLSearchParams()
      data.set('infer', String(false))
      data.set('sameAs', String(false))
      data.set('query', query)

      const response = await axios({
        method: 'post',
        url: this.sparqlEndpoint,
        maxContentLength: Infinity,
        maxBodyLength: Infinity,
        data,
        // @ts-expect-error
        withCredentials: document.withCredentials ?? true,
        headers: {
          Accept: 'text/turtle,application/n-triples,*/*;q=0.9',
          'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
        },
        cancelToken: new axios.CancelToken((c) => this.cancelActiveRequest = c),
      })

      this.cancelActiveRequest = null

      const timestamp = new Date()
      registerEventLog({
        type: 'sparql-request-event',
        request: query,
        templateId: this.templateId,
        response: response.data,
        timestamp: formatTimestamp(timestamp),
        namespace: this.namespace,
      })

      return response.data as string
    } catch (error) {
      if (isAxiosError(error) && (error.response?.status === 401 || error.response?.status === 403))
        throw new ErrorNeedAuth()
      console.error('Error fetching turtle:', error)
      throw error
    }
  }
}

async function waitFor(ms: number): Promise<void> {
  return new Promise(resolve => {
    setTimeout(() => resolve(), ms)
  })
}

function shouldCacheQuery(query: QueryString) {
  return !query.includes("#nocache")
}
